<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="loading && !selectedLcData">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Loading...</p>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header mb-4">
                GRN Entry Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form ref="gnrForm">
                  <v-row>
                    <!-- Left Column: LC & Shipment Selection -->
                    <v-col md="4" cols="12">
                      <v-expansion-panels v-model="panel1" multiple>
                        <v-expansion-panel>
                          <v-expansion-panel-title class="panel-header mb-4">
                            LC & Shipment Selection
                          </v-expansion-panel-title>
                          <v-expansion-panel-text>
                            <v-autocomplete
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="lc_id.value.value"
                              :items="getLcImportLcMasterList"
                              :item-title="item => `${item.lc_no} - ${item.code || ''}`"
                              item-value="id"
                              :error-messages="lc_id.errorMessage.value"
                              @update:modelValue="loadLcData"
                              clearable
                            >
                              <template #label>
                                Select LC <span class="text-red-accent-4">*</span>
                              </template>
                            </v-autocomplete>

                            <v-autocomplete
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="shipment_id.value.value"
                              :items="availableShipments"
                              :item-title="item => `Shipment #${item.shipment_no} - ${formatDate(item.shipment_date)}`"
                              item-value="id"
                              :error-messages="shipment_id.errorMessage.value"
                              @update:modelValue="loadShipmentData"
                              :disabled="!lc_id.value.value"
                              clearable
                            >
                              <template #label>
                                Select Shipment <span class="text-red-accent-4">*</span>
                              </template>
                            </v-autocomplete>

                            <v-text-field
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="gnr_no.value.value"
                              :error-messages="gnr_no.errorMessage.value"
                              label="GRN No"
                              required
                            >
                              <template #label>
                                GRN No <span class="text-red-accent-4">*</span>
                              </template>
                            </v-text-field>

                            <v-date-input
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="gnr_date.value.value"
                              :error-messages="gnr_date.errorMessage.value"
                              label="GRN Date"
                              prepend-icon=""
                              prepend-inner-icon="mdi-calendar"
                              required
                            >
                              <template #label>
                                GRN Date <span class="text-red-accent-4">*</span>
                              </template>
                            </v-date-input>

                            <v-textarea
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="remarks.value.value"
                              label="Remarks"
                              rows="3"
                            ></v-textarea>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>

                    <!-- Right Column: Shipment Items & GRN Items -->
                    <v-col md="8" cols="12">
                      <!-- Alert Messages - Always Visible -->
                      <v-alert
                        v-if="!lc_id.value.value"
                        type="info"
                        variant="tonal"
                        prominent
                        icon="mdi-information"
                        class="mb-3"
                      >
                        <strong>Step 1:</strong> Please select an LC to continue
                      </v-alert>
                      
                      <v-alert
                        v-else-if="!shipment_id.value.value"
                        type="info"
                        variant="tonal"
                        prominent
                        icon="mdi-information"
                        class="mb-3"
                      >
                        <strong>Step 2:</strong> Please select a Shipment to view GRN items
                      </v-alert>
                      
                      <v-alert
                        v-else-if="selectedShipmentData && shipmentItemsForGnr.length === 0"
                        type="warning"
                        variant="tonal"
                        prominent
                        icon="mdi-alert"
                        class="mb-3"
                      >
                        <strong>No Items Found:</strong> The selected shipment has no items. Please select a different shipment.
                      </v-alert>
                      
                      <v-alert
                        v-else-if="selectedShipmentData && shipmentItemsForGnr.length > 0 && selectedItems.length === 0"
                        type="warning"
                        variant="tonal"
                        prominent
                        icon="mdi-alert"
                        class="mb-3"
                      >
                        <strong>Step 3:</strong> Please enter quantities (Received, Accepted, or Rejected) for at least one item in the table below
                      </v-alert>

                      <!-- GRN Items Section -->
                      <v-card v-if="selectedShipmentData" class="mb-3">
                        <v-card-title class="bg-grey-lighten-4">
                          <span class="panel-header">GRN Items</span>
                        </v-card-title>
                        <v-card-text>
                          <div v-if="!selectedShipmentData.shipmentItems && !selectedShipmentData.shipment_items" class="text-center py-4">
                            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                            <p class="mt-2 text-grey">Loading shipment items...</p>
                          </div>
                          
                          <div v-else-if="shipmentItemsForGnr.length === 0" class="text-center py-4">
                            <v-alert type="warning" variant="tonal">
                              No items found in this shipment. Please select a different shipment or add items to this shipment first.
                            </v-alert>
                          </div>
                          
                          <div v-else>

                            <v-data-table
                              :headers="gnrItemHeaders"
                              :items="shipmentItemsForGnr"
                              density="compact"
                              class="elevation-0"
                              :items-per-page="-1"
                              hide-default-footer
                            >
                              <template v-slot:item.product="{ item }">
                                {{ item.product?.name || '-' }} ({{ item.product?.code || '-' }})
                              </template>

                              <template v-slot:item.shipment_qty="{ item }">
                                {{ formatNumber(item.qty) }}
                              </template>

                              <template v-slot:item.received_qty="{ item }">
                                <v-text-field
                                  v-model="getGnrItem(item.id).received_qty"
                                  type="number"
                                  density="compact"
                                  variant="outlined"
                                  :max="item.qty"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="updateGnrItem(item)"
                                ></v-text-field>
                              </template>

                              <template v-slot:item.accepted_qty="{ item }">
                                <v-text-field
                                  v-model="getGnrItem(item.id).accepted_qty"
                                  type="number"
                                  density="compact"
                                  variant="outlined"
                                  :max="getGnrItem(item.id).received_qty"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="updateGnrItem(item)"
                                ></v-text-field>
                              </template>

                              <template v-slot:item.rejected_qty="{ item }">
                                <v-text-field
                                  v-model="getGnrItem(item.id).rejected_qty"
                                  type="number"
                                  density="compact"
                                  variant="outlined"
                                  :max="getGnrItem(item.id).received_qty"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="updateGnrItem(item)"
                                ></v-text-field>
                              </template>

                              <template v-slot:item.rate="{ item }">
                                <v-text-field
                                  v-model="getGnrItem(item.id).rate"
                                  type="number"
                                  density="compact"
                                  variant="outlined"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="updateGnrItem(item)"
                                ></v-text-field>
                              </template>

                              <template v-slot:item.amount="{ item }">
                                {{ formatCurrency(getGnrItem(item.id).amount) }}
                              </template>
                            </v-data-table>

                            <v-card variant="outlined" class="mt-3" v-if="selectedItems.length > 0">
                              <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
                                GRN Summary
                              </v-card-title>
                              <v-card-text>
                                <v-row>
                                  <v-col cols="12" md="6">
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Total Items:</strong> {{ selectedItems.length }}
                                    </div>
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Total Received Qty:</strong> {{ formatNumber(totalReceivedQty) }}
                                    </div>
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Total Accepted Qty:</strong> {{ formatNumber(totalAcceptedQty) }}
                                    </div>
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Total Rejected Qty:</strong> {{ formatNumber(totalRejectedQty) }}
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="6">
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Total GRN Value:</strong> {{ formatCurrency(totalGnrValue) }}
                                    </div>
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-card>
                          </div>
                        </v-card-text>
                      </v-card>

                      <!-- Action Buttons - Always Visible -->
                      <v-row class="mt-3">
                        <v-col cols="12">
                          <div class="d-flex justify-end" style="gap: 12px;">
                            <v-btn
                              class="bg-grey-darken-1"
                              variant="flat"
                              type="button"
                              :loading="submitting && currentStatus === 0"
                              :disabled="submitting && currentStatus !== 0 || selectedItems.length === 0"
                              @click="submitForm(0)"
                            >
                              DRAFT
                            </v-btn>
                            <v-btn
                              class="bg-primary"
                              variant="flat"
                              type="button"
                              :loading="submitting && currentStatus === 1"
                              :disabled="submitting && currentStatus !== 1 || selectedItems.length === 0"
                              @click="submitForm(1)"
                            >
                              POST
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formatDateView from "@/plugins/dateFormatView";
import { useField, useForm } from "vee-validate";
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import * as yup from "yup";

export default {
  name: "LcImportGnr",
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const panel = ref([0]);
    const panel1 = ref([0]);
    const panel2 = ref([0]);
    const loading = ref(false);
    const submitting = ref(false);
    const currentStatus = ref(0);
    const selectedLcData = ref(null);
    const selectedShipmentData = ref(null);
    const availableShipments = ref([]);
    const gnrItems = ref({}); // Key: shipment_item_id, Value: { received_qty, accepted_qty, rejected_qty, rate, amount }

    const validationSchema = yup.object({
      lc_id: yup.number().required("LC is required"),
      shipment_id: yup.number().required("Shipment is required"),
      gnr_no: yup.string().required("GRN No is required"),
      gnr_date: yup.date().required("GRN Date is required"),
    });

    const { handleSubmit, resetForm: resetFormValidation } = useForm({
      validationSchema,
    });

    const lc_id = useField("lc_id");
    const shipment_id = useField("shipment_id");
    const gnr_no = useField("gnr_no");
    const gnr_date = useField("gnr_date");
    const remarks = useField("remarks");

    const getLcImportLcMasterList = computed(() => {
      return store.getters.getLcImportLcMasterList || [];
    });

    const shipmentItemsForGnr = computed(() => {
      if (!selectedShipmentData.value) {
        return [];
      }
      // Check both camelCase and snake_case property names
      const items = selectedShipmentData.value.shipmentItems || selectedShipmentData.value.shipment_items || [];
      if (!items || items.length === 0) {
        return [];
      }
      return items.map(item => ({
        id: item.id,
        shipment_item_id: item.id,
        product: item.product,
        product_id: item.product_id,
        qty: parseFloat(item.qty || item.received_qty || 0),
        rate: parseFloat(item.rate || 0),
      }));
    });

    const selectedItems = computed(() => {
      const items = Object.values(gnrItems.value).filter(
        item => item.received_qty > 0 || item.accepted_qty > 0 || item.rejected_qty > 0
      );
      return items;
    });

    const totalReceivedQty = computed(() => {
      return selectedItems.value.reduce((sum, item) => sum + parseFloat(item.received_qty || 0), 0);
    });

    const totalAcceptedQty = computed(() => {
      return selectedItems.value.reduce((sum, item) => sum + parseFloat(item.accepted_qty || 0), 0);
    });

    const totalRejectedQty = computed(() => {
      return selectedItems.value.reduce((sum, item) => sum + parseFloat(item.rejected_qty || 0), 0);
    });

    const totalGnrValue = computed(() => {
      return selectedItems.value.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    });

    const gnrItemHeaders = [
      { title: "Product", key: "product", sortable: false },
      { title: "Shipment Qty", key: "shipment_qty", align: "end", sortable: false },
      { title: "Received Qty", key: "received_qty", align: "end", sortable: false },
      { title: "Accepted Qty", key: "accepted_qty", align: "end", sortable: false },
      { title: "Rejected Qty", key: "rejected_qty", align: "end", sortable: false },
      { title: "Rate", key: "rate", align: "end", sortable: false },
      { title: "Amount", key: "amount", align: "end", sortable: false },
    ];

    const loadLcData = async () => {
      if (!lc_id.value.value) {
        selectedLcData.value = null;
        availableShipments.value = [];
        selectedShipmentData.value = null;
        shipment_id.value.value = null;
        return;
      }

      loading.value = true;
      try {
        await store.dispatch("getLcImportLcMasterById", lc_id.value.value);
        const lcData = store.getters.getEditingLcImportLcMaster;
        selectedLcData.value = lcData;

        // Load shipments for this LC
        await store.dispatch("getLcImportLcShipmentList");
        const allShipments = store.getters.getLcImportLcShipmentList;
        availableShipments.value = allShipments.filter(
          s => s.lc_id === lc_id.value.value
        );
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load LC data");
      } finally {
        loading.value = false;
      }
    };

    const loadShipmentData = async () => {
      if (!shipment_id.value.value) {
        selectedShipmentData.value = null;
        gnrItems.value = {};
        return;
      }

      loading.value = true;
      try {
        await store.dispatch("getLcImportLcShipmentById", shipment_id.value.value);
        const shipmentData = store.getters.getEditingLcImportLcShipment;
        selectedShipmentData.value = shipmentData;

        // Initialize GRN items from shipment items (check both property names)
        const items = shipmentData.shipmentItems || shipmentData.shipment_items || [];
        if (items && items.length > 0) {
          items.forEach(item => {
            if (!gnrItems.value[item.id]) {
              gnrItems.value[item.id] = {
                shipment_item_id: item.id,
                product_id: item.product_id,
                received_qty: 0,
                accepted_qty: 0,
                rejected_qty: 0,
                rate: parseFloat(item.rate || 0),
                amount: 0,
              };
            }
          });
          // Auto-expand the GRN Items panel when shipment data loads
          await nextTick();
          if (panel2.value.length === 0) {
            panel2.value = [0];
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load shipment data");
      } finally {
        loading.value = false;
      }
    };

    const getGnrItem = (shipmentItemId) => {
      if (!gnrItems.value[shipmentItemId]) {
        gnrItems.value[shipmentItemId] = {
          shipment_item_id: shipmentItemId,
          product_id: null,
          received_qty: 0,
          accepted_qty: 0,
          rejected_qty: 0,
          rate: 0,
          amount: 0,
        };
      }
      return gnrItems.value[shipmentItemId];
    };

    const updateGnrItem = (shipmentItem) => {
      const gnrItem = getGnrItem(shipmentItem.id);
      
      if (!gnrItem.product_id) {
        gnrItem.product_id = shipmentItem.product_id;
      }

      // Validate quantities
      const receivedQty = parseFloat(gnrItem.received_qty || 0);
      const acceptedQty = parseFloat(gnrItem.accepted_qty || 0);
      const rejectedQty = parseFloat(gnrItem.rejected_qty || 0);
      const maxQty = parseFloat(shipmentItem.qty || 0);

      // Ensure received_qty doesn't exceed shipment qty
      if (receivedQty > maxQty) {
        gnrItem.received_qty = maxQty;
        toast.warning(`Received quantity cannot exceed shipment quantity (${maxQty})`);
      }

      // Ensure accepted_qty + rejected_qty <= received_qty
      if (acceptedQty + rejectedQty > receivedQty) {
        const remaining = receivedQty - acceptedQty;
        gnrItem.rejected_qty = Math.max(0, remaining);
        toast.warning("Accepted + Rejected quantity cannot exceed Received quantity");
      }

      // Calculate amount based on accepted_qty
      const finalAcceptedQty = parseFloat(gnrItem.accepted_qty || 0);
      const rate = parseFloat(gnrItem.rate || 0);
      gnrItem.amount = finalAcceptedQty * rate;
    };

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateView(date);
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return "0.00";
      const currencySymbol = selectedLcData.value?.currency?.symbol || '';
      return currencySymbol ? `${currencySymbol} ${Number(amount).toFixed(2)}` : Number(amount).toFixed(2);
    };

    const toYmd = (val) => {
      if (!val) return null;
      if (typeof val === "string") {
        return val.split("T")[0];
      }
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString().split("T")[0];
    };

    const submitForm = async (btnStatus) => {
      currentStatus.value = btnStatus;

      if (!lc_id.value.value || !shipment_id.value.value || !gnr_no.value.value || !gnr_date.value.value) {
        toast.error("Please fill in all required fields");
        return;
      }

      if (selectedItems.value.length === 0) {
        toast.error("Please enter quantities for at least one item");
        return;
      }

      // Validate items
      for (const item of selectedItems.value) {
        const receivedQty = parseFloat(item.received_qty || 0);
        const acceptedQty = parseFloat(item.accepted_qty || 0);
        const rejectedQty = parseFloat(item.rejected_qty || 0);

        if (receivedQty <= 0) {
          toast.error("Received quantity must be greater than 0");
          return;
        }

        if (acceptedQty + rejectedQty > receivedQty) {
          toast.error("Accepted + Rejected quantity cannot exceed Received quantity");
          return;
        }

        if (parseFloat(item.rate || 0) <= 0) {
          toast.error("Rate must be greater than 0");
          return;
        }
      }

      submitting.value = true;
      try {
        const formData = {
          lc_id: lc_id.value.value,
          shipment_id: shipment_id.value.value,
          supplier_id: selectedLcData.value?.supplier_id || null,
          gnr_no: gnr_no.value.value,
          gnr_date: toYmd(gnr_date.value.value),
          remarks: remarks.value.value || null,
          status: btnStatus,
          items: selectedItems.value.map(item => ({
            shipment_item_id: item.shipment_item_id,
            product_id: item.product_id,
            received_qty: parseFloat(item.received_qty),
            accepted_qty: parseFloat(item.accepted_qty),
            rejected_qty: parseFloat(item.rejected_qty),
            rate: parseFloat(item.rate),
            amount: parseFloat(item.amount),
          })),
        };

        await store.dispatch('saveUpdateLcImportGnr', formData);
        toast.success(btnStatus === 0 ? "GRN saved as draft successfully" : "GRN posted successfully");
        router.push({ name: 'lc-import-gnr-records' });
      } catch (error) {
        toast.error(error.response?.data?.message || error.response?.data?.error || `Failed to ${btnStatus === 0 ? 'save draft' : 'post GRN'}`);
      } finally {
        submitting.value = false;
      }
    };

    const resetForm = () => {
      if (confirm("Are you sure you want to clear all GRN data? This action cannot be undone.")) {
        resetFormValidation();
        selectedLcData.value = null;
        selectedShipmentData.value = null;
        availableShipments.value = [];
        gnrItems.value = {};
        lc_id.value.value = null;
        shipment_id.value.value = null;
        gnr_no.value.value = null;
        gnr_date.value.value = null;
        remarks.value.value = null;
        
        // Reset panels
        panel.value = [0];
        panel1.value = [0];
        panel2.value = [];
        
        toast.success("GRN form cleared successfully");
      }
    };

    const loadExistingData = async () => {
      if (route.params.id) {
        loading.value = true;
        try {
          await store.dispatch("getLcImportGnrById", route.params.id);
          const gnrData = store.getters["getEditingLcImportGnr"];
          
          if (gnrData) {
            // Set form fields
            lc_id.value.value = gnrData.lc_id;
            shipment_id.value.value = gnrData.shipment_id;
            gnr_no.value.value = gnrData.gnr_no;
            gnr_date.value.value = gnrData.gnr_date;
            remarks.value.value = gnrData.remarks || null;
            
            // Load LC and shipment data first
            await loadLcData();
            await loadShipmentData();
            
            // Load GRN details if available (check both camelCase and snake_case)
            const gnrDetails = gnrData.gnrDetails || gnrData.gnr_details || [];
            if (gnrDetails.length > 0) {
              // Wait for shipment data to load first so gnrItems are initialized
              await nextTick();
              
              gnrDetails.forEach(detail => {
                const shipmentItemId = detail.shipment_item_id;
                if (shipmentItemId) {
                  // Initialize if not exists
                  if (!gnrItems.value[shipmentItemId]) {
                    gnrItems.value[shipmentItemId] = {
                      shipment_item_id: shipmentItemId,
                      product_id: detail.product_id,
                      received_qty: 0,
                      accepted_qty: 0,
                      rejected_qty: 0,
                      rate: 0,
                      amount: 0,
                    };
                  }
                  // Update with existing values
                  gnrItems.value[shipmentItemId] = {
                    ...gnrItems.value[shipmentItemId],
                    received_qty: parseFloat(detail.received_qty || 0),
                    accepted_qty: parseFloat(detail.accepted_qty || 0),
                    rejected_qty: parseFloat(detail.rejected_qty || 0),
                    rate: parseFloat(detail.rate || 0),
                    amount: parseFloat(detail.amount || 0),
                  };
                }
              });
            }
          }
        } catch (error) {
          toast.error("Failed to load GRN data");
          router.push({ name: "lc-import-gnr-records" });
        } finally {
          loading.value = false;
        }
      }
    };

    onMounted(async () => {
      await store.dispatch('getLcImportLcMasterList');
      await store.dispatch('getLcImportLcShipmentList');
      await loadExistingData();
    });

    return {
      panel,
      panel1,
      panel2,
      loading,
      submitting,
      currentStatus,
      selectedLcData,
      selectedShipmentData,
      availableShipments,
      lc_id,
      shipment_id,
      gnr_no,
      gnr_date,
      remarks,
      getLcImportLcMasterList,
      shipmentItemsForGnr,
      selectedItems,
      totalReceivedQty,
      totalAcceptedQty,
      totalRejectedQty,
      totalGnrValue,
      gnrItemHeaders,
      loadLcData,
      loadShipmentData,
      getGnrItem,
      updateGnrItem,
      formatDate,
      formatNumber,
      formatCurrency,
      submitForm,
      resetForm,
    };
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

